import { useEffect, useState, useCallback, useRef } from "react";
import { cn } from "@/lib/utils";

export interface TOCSection {
  id: string;
  title: string;
  level: number;
}

interface TableOfContentsProps {
  sections: TOCSection[];
  className?: string;
}

export function TableOfContents({ sections, className }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id || "");
  const [isSticky, setIsSticky] = useState(false);
  const tocRef = useRef<HTMLDivElement>(null);

  // Set up intersection observer to track active section
  useEffect(() => {
    if (sections.length === 0) return;

    const observerOptions = {
      rootMargin: "-100px 0px -40% 0px",
      threshold: 0,
    };

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      // Find the entry that is most visible
      const visibleEntry = entries.find(entry => entry.isIntersecting);
      if (visibleEntry) {
        setActiveId(visibleEntry.target.id);
      }
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    sections.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    // Also check if we're near the bottom of the page - if so, activate last section
    const handleScroll = () => {
      const scrolledToBottom = 
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 100;
      if (scrolledToBottom && sections.length > 0) {
        setActiveId(sections[sections.length - 1].id);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
    };
  }, [sections]);

  // Track when TOC goes out of view for sticky behavior
  useEffect(() => {
    if (sections.length === 0 || !tocRef.current) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show sticky when TOC is not visible (scrolled past it)
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: "-50px 0px 0px 0px" }
    );

    observer.observe(tocRef.current);
    return () => observer.disconnect();
  }, [sections]);

  const scrollToSection = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -60;
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
      setActiveId(id);
    }
  }, []);

  if (sections.length === 0) return null;

  const activeSection = sections.find(s => s.id === activeId) || sections[0];

  return (
    <>
      {/* Static table of contents at top of post - always in DOM for position tracking */}
      <nav
        ref={tocRef}
        id="table-of-contents"
        className={cn("", className)}
      >
        <div className="flex flex-col gap-1 py-4">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className={cn(
                "text-left text-base transition-colors duration-200 focus:outline-none",
                activeId === section.id
                  ? "text-[#000000] font-semibold"
                  : "text-black/50 hover:text-black",
                section.level === 3 && "text-sm pl-4"
              )}
            >
              {section.title}
            </button>
          ))}
        </div>
      </nav>
      
      {/* Sticky header - shows only current section, positioned below navbar */}
      {isSticky && (
        <div className="fixed top-[41px] md:top-[48px] left-0 right-0 z-40 bg-white shadow-sm">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-2">
            <span className="text-sm font-semibold text-[#000000]">
              {activeSection?.title}
            </span>
          </div>
        </div>
      )}
    </>
  );
}

// Helper function to generate slug from heading text
export function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}
